import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, getDocs, limit, doc, getDoc } from 'firebase/firestore';
import Navbar from '../components/Navbar';

// Helper function to get initials
const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Canadian provinces and territories data
const provinceNames: { [key: string]: string } = {
  'AB': 'Alberta',
  'BC': 'British Columbia',
  'MB': 'Manitoba',
  'NB': 'New Brunswick',
  'NL': 'Newfoundland and Labrador',
  'NS': 'Nova Scotia',
  'NT': 'Northwest Territories',
  'NU': 'Nunavut',
  'ON': 'Ontario',
  'PE': 'Prince Edward Island',
  'QC': 'Quebec',
  'SK': 'Saskatchewan',
  'YT': 'Yukon'
};

const Dashboard = () => {
  const { currentUser, userDetails, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // User stats
  const [userTotalMiles, setUserTotalMiles] = useState<number>(0);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [teamName, setTeamName] = useState<string>('');
  const [teamTotalMiles, setTeamTotalMiles] = useState<number>(0);
  const [teamRank, setTeamRank] = useState<number | null>(null);
  
  // Leaderboards
  const [topIndividuals, setTopIndividuals] = useState<any[]>([]);
  const [topTeams, setTopTeams] = useState<any[]>([]);
  const [provincesRanking, setProvincesRanking] = useState<any[]>([]);
  
  // Recent logs
  const [recentLogs, setRecentLogs] = useState<any[]>([]);
  
  // Dashboard loading state
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentUser || !userDetails) return;
      
      try {
        setLoading(true);
        
        // Get user's miles
        const milesRef = collection(db, 'miles');
        const userMilesQuery = query(
          milesRef, 
          where('userId', '==', currentUser.uid),
          orderBy('date', 'desc')
        );
        const milesSnapshot = await getDocs(userMilesQuery);
        
        let total = 0;
        const recentMilesLogs: any[] = [];
        
        milesSnapshot.forEach((doc) => {
          const data = doc.data();
          total += Number(data.miles);
          if (recentMilesLogs.length < 5) {
            recentMilesLogs.push({
              id: doc.id,
              ...data
            });
          }
        });
        
        setUserTotalMiles(total);
        setRecentLogs(recentMilesLogs);
        
        // Get team info if user is in a team
        if (userDetails.teamId) {
          const teamDoc = await getDoc(doc(db, 'teams', userDetails.teamId));
          if (teamDoc.exists()) {
            const teamData = teamDoc.data();
            setTeamName(teamData.name || 'Team Member');
            setTeamTotalMiles(teamData.totalMiles || 0);
            
            // Get team rank
            const teamsRef = collection(db, 'teams');
            const teamRankQuery = query(
              teamsRef,
              orderBy('totalMiles', 'desc')
            );
            const teamRankSnapshot = await getDocs(teamRankQuery);
            
            let rank = 1;
            teamRankSnapshot.forEach((doc) => {
              if (doc.id === userDetails.teamId) {
                setTeamRank(rank);
              }
              rank++;
            });
          } else {
            setTeamName('Team Member (not found)');
          }
        } else {
          setTeamName('Individual Participant');
        }
        
        // Get top 5 individuals
        const usersRef = collection(db, 'users');
        const topUsersQuery = query(
          usersRef,
          orderBy('totalMiles', 'desc'),
          limit(5)
        );
        const topUsersSnapshot = await getDocs(topUsersQuery);
        
        const topUsers: any[] = [];
        topUsersSnapshot.forEach((doc) => {
          const data = doc.data();
          topUsers.push({
            id: doc.id,
            name: `${data.firstName} ${data.lastName}`,
            province: data.province,
            miles: data.totalMiles || 0
          });
          
          // Check if current user is in top 5
          if (doc.id === currentUser.uid) {
            setUserRank(topUsers.length);
          }
        });
        
        setTopIndividuals(topUsers);
        
        // If user is not in top 5, get their rank
        if (userRank === null) {
          const userRankQuery = query(
            usersRef,
            orderBy('totalMiles', 'desc')
          );
          const userRankSnapshot = await getDocs(userRankQuery);
          
          let rank = 1;
          userRankSnapshot.forEach((doc) => {
            if (doc.id === currentUser.uid) {
              setUserRank(rank);
            }
            rank++;
          });
        }
        
        // Get top 5 teams
        const teamsRef = collection(db, 'teams');
        const topTeamsQuery = query(
          teamsRef,
          orderBy('totalMiles', 'desc'),
          limit(5)
        );
        const topTeamsSnapshot = await getDocs(topTeamsQuery);
        
        const topTeamsList: any[] = [];
        topTeamsSnapshot.forEach((doc) => {
          const data = doc.data();
          topTeamsList.push({
            id: doc.id,
            name: data.name,
            province: data.province,
            miles: data.totalMiles || 0,
            memberCount: data.memberCount || 0
          });
        });
        
        setTopTeams(topTeamsList);
        
        // Get province rankings
        const provincesRef = collection(db, 'provinces');
        const provincesQuery = query(
          provincesRef,
          orderBy('totalMiles', 'desc')
        );
        const provincesSnapshot = await getDocs(provincesQuery);
        
        const provincesList: any[] = [];
        provincesSnapshot.forEach((doc) => {
          const data = doc.data();
          provincesList.push({
            id: doc.id,
            name: doc.id,
            miles: data.totalMiles || 0,
            participantCount: data.participantCount || 0
          });
        });
        
        setProvincesRanking(provincesList);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setTeamName('Error loading team');
        setLoading(false);
      }
    };
    
    if (!authLoading) {
      fetchDashboardData();
    }
  }, [currentUser, userDetails, authLoading]);
  
  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/login');
    }
  }, [authLoading, currentUser, navigate]);
  
  if (authLoading || !userDetails) {
    return (
      <div className="min-h-screen bg-background dark:bg-gray-900 flex items-center justify-center">
        <Navbar />
        <div className="text-center">
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading Dashboard...</p>
          {/* Add a spinner here if desired */}
        </div>
      </div>
    );
  }
  
  const userProvinceName = provinceNames[userDetails.province] || userDetails.province;

  return (
    <div className="min-h-screen bg-background dark:bg-gray-900">
      <Navbar />
      
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center">
          <div className="flex items-center mb-4 sm:mb-0">
            <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary dark:bg-indigo-500">
              <span className="text-xl font-medium leading-none text-white">
                {getInitials(userDetails.firstName, userDetails.lastName)}
              </span>
            </span>
            <div className="ml-4">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Welcome, {userDetails.firstName}!
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {loading ? "Loading details..." : `${teamName} | ${userProvinceName}`}
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Link to="/invite" className="btn btn-secondary btn-sm">
              Invite Friends
            </Link>
            <Link to="/profile" className="btn btn-outline btn-sm">
               View Profile & Settings
            </Link>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        {/* Quick Actions */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Log Miles Card */}
            <div className="card bg-white dark:bg-gray-800">
              <div className="card-body">
                <h3 className="card-title text-lg font-medium text-primary dark:text-indigo-400">Log Your Miles</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Keep track of your progress towards the Million Miles goal.</p>
                <Link to="/miles" className="btn btn-primary w-full">
                  Log Miles Now
                </Link>
              </div>
            </div>

            {/* Donate Card */}
            <div className="card bg-white dark:bg-gray-800">
              <div className="card-body">
                <h3 className="card-title text-lg font-medium text-secondary dark:text-sky-400">Make a Donation</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Support HealthPartners and 20 vital charities across Canada.</p>
                <Link to="/donate" className="btn btn-secondary w-full">
                  Donate Today
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        {/* Progress Summary */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Your Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Miles Summary */}
            <div className="card bg-white dark:bg-gray-800">
               <div className="card-body">
                  <h3 className="card-title text-lg font-medium">Miles Logged</h3>
                  <p className="text-3xl font-bold text-primary dark:text-indigo-400">{userTotalMiles || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total miles contributed</p>
               </div>
            </div>

            {/* Fundraising Summary */}
            <div className="card bg-white dark:bg-gray-800">
               <div className="card-body">
                 <h3 className="card-title text-lg font-medium">Funds Raised</h3>
                  <p className="text-3xl font-bold text-secondary dark:text-sky-400">${(userDetails.totalRaised || 0).toFixed(2)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total funds raised through your invites</p>
               </div>
            </div>
          </div>
        </section>
        
        {/* Add more sections as needed, e.g., Recent Activity, Team Progress */}

      </main>
    </div>
  );
};

export default Dashboard; 